import React from 'react';
import {
  Row,
  Col,
  Input,
  InputGroup,
  Button,
  FormFeedback,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';

import AppButton from 'components/App/Button';
import AppInputValidation from 'components/App/InputValidation';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import FormField from 'components/AddLocation/FormField';
import FormFieldCheckbox from 'components/AddLocation/FormFieldCheckbox';
import FormFieldWithLine from 'components/AddLocation/FormFieldWithLine';
import SectionWrapper from 'components/AddLocation/SectionWrapper';

import { ReactComponent as NextIconSVG } from 'assets/images/location/next-icon.svg';
import { ReactComponent as PrevIconSVG } from 'assets/images/location/prev-icon.svg';

import TAB from 'constants/tab';
import { PARAM_SETTING } from 'constants/setting';
import { editLocationFormSteps } from 'utils/validate';

import styles from './StepRoomInformation.module.scss';

const CHANGE_QUANTITY_STATUS = {
  INCREASE: 'INCREASE',
  REDUCE: 'REDUCE',
};

function StepRoomInformation(props) {
  const {
    onBack, actionAddLocationByStep, setting, location, setIsValid,
  } = props;

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      roomName: '',
      name: '',
      size: '',
      quantity: 1,
      totalBedrooms: 0,
      totalLivingRooms: 0,
      totalBathrooms: 0,
      bedTypes: setting[PARAM_SETTING.BED_TYPES]?.[0].children.map((bed) => ({
        bedType: bed.name,
        quantity: 0,
        checked: false,
        vi: bed.vi,
      })),
      maxAdults: 2,
      maxChildren: 0,
      standardGuests: 2,
      maxCapacity: 4,
      priceBase: '',
    },

    validationSchema: Yup.object(editLocationFormSteps.stepRoomInformation),

    onSubmit: (values) => {
      const locationOfStep = {
        room: {
          roomName: '',
          name: values.name,
          size: values.size === '' ? 0 : values.size,
          quantity: values.quantity,
          totalBedrooms: values.totalBedrooms === '' ? 0 : values.totalBedrooms,
          totalLivingRooms: values.totalLivingRooms === '' ? 0 : values.totalLivingRooms,
          totalBathrooms: values.totalBathrooms === '' ? 0 : values.totalBathrooms,
          bedTypes: values.bedTypes,
          standardGuests: values.standardGuests,
          maxCapacity: values.maxCapacity,
          maxAdults: values.maxAdults,
          maxChildren: values.maxChildren,
          priceBase: values.priceBase,
        },
      };

      actionAddLocationByStep({
        step: TAB.AMENITIES_SERVICES,
        location: locationOfStep,
      });
    },
  });

  const onChangeInputField = React.useCallback((e) => {
    validation.setFieldValue(e.target.name, e.target.value);
    actionAddLocationByStep({
      step: TAB.ROOM_PRICE,
      location: {
        ...location,
        room: {
          ...location.room,
          roomName: '',
          [e.target.name]: e.target.value,
        },
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const onChangeNumber = React.useCallback((e) => {
    const regex = /[^0-9]/g;
    const finalValue = parseFloat(e.target.value.trim().replace(regex, ''));

    validation.setFieldValue(e.target.name, finalValue, true);

    actionAddLocationByStep({
      step: TAB.ROOM_PRICE,
      location: {
        ...location,
        room: {
          ...location.room,
          [e.target.name]: finalValue,
        },
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const totalGuestValue = React.useMemo(() => validation.values.maxAdults
    + validation.values.maxChildren, [validation.values]);

  const onUpdateAmount = React.useCallback((status, name, valueAmount) => () => {
    const newValueAmount = () => {
      if (status === CHANGE_QUANTITY_STATUS.INCREASE) {
        return valueAmount + 1;
      }

      if (status === CHANGE_QUANTITY_STATUS.REDUCE) {
        return valueAmount > 0 ? valueAmount - 1 : 0;
      }

      return valueAmount;
    };

    validation.setFieldValue(name, newValueAmount());

    actionAddLocationByStep({
      step: TAB.ROOM_PRICE,
      location: {
        ...location,
        room: {
          ...location.room,
          [name]: newValueAmount(),
        },
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const onUpdateBedQuantity = React.useCallback((name, status) => () => {
    const newBedQuantity = validation.values.bedTypes.map((bed) => {
      if (status === CHANGE_QUANTITY_STATUS.REDUCE) {
        if (bed.bedType === name) {
          return ({
            ...bed,
            quantity: bed.quantity > 0 ? bed.quantity - 1 : bed.quantity,
          });
        }

        return bed;
      }

      if (status === CHANGE_QUANTITY_STATUS.INCREASE) {
        if (bed.bedType === name) {
          return ({
            ...bed,
            quantity: bed.quantity + 1,
          });
        }
      }

      return bed;
    });

    validation.setFieldValue('bedTypes', newBedQuantity, true);

    actionAddLocationByStep({
      step: TAB.ROOM_PRICE,
      location: {
        ...location,
        room: {
          ...location.room,
          bedTypes: newBedQuantity,
        },
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const onChangeCheckedBed = React.useCallback((e) => {
    const newBedChecked = validation.values.bedTypes.map((bed) => {
      if (bed.bedType === e.target.name) {
        return ({
          ...bed,
          checked: e.target.checked,
        });
      }

      return bed;
    });

    validation.setFieldValue('bedTypes', newBedChecked, true);

    actionAddLocationByStep({
      step: TAB.ROOM_PRICE,
      location: {
        ...location,
        room: {
          ...location.room,
          bedTypes: newBedChecked,
        },
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  React.useEffect(() => {
    setIsValid(validation.isValid);
  }, [setIsValid, validation.isValid]);

  return (
    <form className={styles.Component} onSubmit={validation.handleSubmit}>
      <SectionWrapper title="Quý vị cần ít nhất một phòng để đăng tải chỗ nghỉ">
        <FormField label="Tên phòng" isRequired>
          <AppInputValidation
            name="name"
            id="name"
            type="text"
            placeholder="Nhập tên phòng"
            validation={validation}
            isCustomOnChange
            onChange={onChangeInputField}
            subTitle="Đây là tên mà khách sẽ thấy trên trang web Bookese.com"
          />
        </FormField>

        {/* <FormField label="Tên tùy chọn">
          <AppInputValidation
            name="name"
            id="name"
            type="text"
            placeholder="Nhập tên tuỳ chọn"
            validation={validation}
            isCustomOnChange
            onChange={onChangeInputField}
          />
        </FormField> */}

        {/* <FormField label="Diện tích phòng">
          <AppInputNumberValidation
            name="size"
            keyName="size"
            validation={validation}
            isCustomOnChange
            onChange={onChangeNumber}
            onBlur={validation.handleBlur}
            showCurrency={
              (
                <span>
                  m
                  <sup>2</sup>
                </span>
              )
            }
          />
        </FormField> */}

        <Row>
          <Col md="6">
            <FormField label="Số phòng (loại này)" isRequired>
              <AppInputNumberValidation
                subTitle="Trong số 38 phòng tại chỗ nghỉ của Quý vị"
                name="quantity"
                keyName="quantity"
                validation={validation}
                isCustomOnChange
                onChange={onChangeNumber}
                onBlur={validation.handleBlur}
              />
            </FormField>
          </Col>

          <Col md="6">
            <FormField label="Số phòng ngủ">
              <AppInputNumberValidation
                name="totalBedrooms"
                keyName="totalBedrooms"
                validation={validation}
                isCustomOnChange
                onChange={onChangeNumber}
                onBlur={validation.handleBlur}
              />
            </FormField>
          </Col>

          <Col md="6" className="mt-24">
            <FormField label="Số phòng khách">
              <AppInputNumberValidation
                name="totalLivingRooms"
                keyName="totalLivingRooms"
                validation={validation}
                isCustomOnChange
                onChange={onChangeNumber}
                onBlur={validation.handleBlur}
              />
            </FormField>
          </Col>

          <Col md="6" className="mt-24">
            <FormField label="Số phòng tắm">
              <AppInputNumberValidation
                name="totalBathrooms"
                keyName="totalBathrooms"
                validation={validation}
                isCustomOnChange
                onChange={onChangeNumber}
                onBlur={validation.handleBlur}
              />
            </FormField>
          </Col>

        </Row>
      </SectionWrapper>

      <SectionWrapper title="Tùy chọn giường">
        {validation.errors?.bedTypes && (
          <FormFeedback type="invalid" className="d-block font-size-12">
            {validation.errors.bedTypes}
          </FormFeedback>
        )}

        {validation.values.bedTypes?.map((bed, index) => (
          <FormFieldCheckbox key={bed.bedType} isHasTopBorder={!!index} noTitle>
            <Row>
              <Col md="12" className="d-flex align-items-center">
                <label htmlFor={bed.bedType} className="flex-1 d-flex align-items-center">
                  <Input
                    type="checkbox"
                    id={bed.bedType}
                    name={bed.bedType}
                    checked={bed.isChecked}
                    onChange={onChangeCheckedBed}
                  />

                  <span className="CheckBoxLabel">
                    {bed.vi}
                  </span>
                </label>

                <InputGroup className="InputGroup">
                  <Button
                    type="button"
                    color="primary"
                    className="InputGroup__Btn InputGroup__Btn--Decrease"
                    onClick={onUpdateBedQuantity(bed.bedType, CHANGE_QUANTITY_STATUS.REDUCE)}
                    disabled={bed.quantity === 0}
                  >
                    <i className="mdi mdi-minus" />
                  </Button>

                  <input
                    type="number"
                    name="parent"
                    className="form-control InputGroup__Input"
                    value={bed.quantity}
                    placeholder="number"
                    readOnly
                  />

                  <Button
                    type="button"
                    color="primary"
                    className="InputGroup__Btn InputGroup__Btn--Increase"
                    onClick={onUpdateBedQuantity(bed.bedType, CHANGE_QUANTITY_STATUS.INCREASE)}
                  >
                    <i className="mdi mdi-plus" />
                  </Button>
                </InputGroup>
              </Col>
            </Row>
          </FormFieldCheckbox>
        ))}
      </SectionWrapper>

      <SectionWrapper title="Tổng sức chứa">
        <FormFieldWithLine label="Số người lớn tối đa" isRequired>
          <InputGroup className="InputGroup">
            <Button
              type="button"
              color="primary"
              className="InputGroup__Btn InputGroup__Btn--Decrease"
              onClick={onUpdateAmount(CHANGE_QUANTITY_STATUS.REDUCE, 'maxAdults', validation.values.maxAdults)}
              disabled={validation.values.maxAdults === 2}
            >
              <i className="mdi mdi-minus" />
            </Button>

            <input
              type="number"
              name="parent"
              className="form-control InputGroup__Input"
              value={validation.values.maxAdults}
              placeholder="number"
              readOnly
            />

            <Button
              type="button"
              color="primary"
              className="InputGroup__Btn InputGroup__Btn--Increase"
              onClick={onUpdateAmount(CHANGE_QUANTITY_STATUS.INCREASE, 'maxAdults', validation.values.maxAdults)}
              disabled={totalGuestValue === 20}
            >
              <i className="mdi mdi-plus" />
            </Button>
          </InputGroup>
        </FormFieldWithLine>

        <FormFieldWithLine label="Số trẻ em tối đa" isRequired>
          <InputGroup className="InputGroup">
            <Button
              type="button"
              color="primary"
              className="InputGroup__Btn InputGroup__Btn--Decrease"
              onClick={onUpdateAmount(CHANGE_QUANTITY_STATUS.REDUCE, 'maxChildren', validation.values.maxChildren)}
              disabled={validation.values.maxChildren === 0}
            >
              <i className="mdi mdi-minus" />
            </Button>

            <input
              type="number"
              name="child"
              className="form-control InputGroup__Input"
              value={validation.values.maxChildren}
              placeholder="number"
              readOnly
            />

            <Button
              type="button"
              color="primary"
              className="InputGroup__Btn InputGroup__Btn--Increase"
              onClick={onUpdateAmount(CHANGE_QUANTITY_STATUS.INCREASE, 'maxChildren', validation.values.maxChildren)}
              disabled={totalGuestValue === 20}
            >
              <i className="mdi mdi-plus" />
            </Button>
          </InputGroup>
        </FormFieldWithLine>

        <FormFieldWithLine label="Tổng số khách tối đa" isRequired subTitle="Tối đa 20 khách">
          <input type="text" value={totalGuestValue} disabled className="text-center" />
        </FormFieldWithLine>
      </SectionWrapper>

      <SectionWrapper title="Giá cơ bản mỗi đêm" isRequired>
        <FormField isHiddenLabel>
          <AppInputNumberValidation
            name="priceBase"
            id="priceBase"
            type="text"
            placeholder="Nhập số tiền"
            validation={validation}
            showCurrency="VNĐ"
            isCustomOnChange
            onChange={onChangeNumber}
          />
        </FormField>
      </SectionWrapper>

      <div className={styles.FormAction}>
        <AppButton
          classes="Btn Btn__PrevStep"
          outline
          onClick={onBack(TAB.BASIC)}
        >
          <PrevIconSVG className="Icon__PrevStep" />
          Quay về bước trước
        </AppButton>

        <AppButton
          classes="Btn Btn__NextStep"
          type="submit"
        >
          Bước tiếp theo
          <NextIconSVG className="Icon__NextStep" />
        </AppButton>
      </div>
    </form>
  );
}

StepRoomInformation.propTypes = {
  setting: PropTypes.instanceOf(Object).isRequired,
  location: PropTypes.instanceOf(Object).isRequired,

  onBack: PropTypes.func.isRequired,
  actionAddLocationByStep: PropTypes.func.isRequired,
  setIsValid: PropTypes.func.isRequired,
};

export default React.memo(StepRoomInformation);
